import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EMPTY, forkJoin, Observable, of, Subject, Subscription, from, throwError } from 'rxjs';
import { catchError, filter, finalize, last, map, mergeMap, shareReplay, take, takeUntil, tap } from 'rxjs/operators';
import { getDescendantOrSelfIds, toOrganizationTree } from 'src/app/_helpers/org-tree-helper';
import { computeShiftLengthHours } from 'src/app/_helpers/time-date-convertor';
import { OrgLevelTypeEnum } from 'src/app/enums/orgLevelType.enum';
import { OrganizationLevel } from 'src/app/models/organizationLevel.model';
import { SegOrgLevel } from 'src/app/models/segOrgLevel.model';
import { Shift } from 'src/app/models/shift.model';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import {
    SegOrgLevelBulkPatchRequest,
    SegOrgLevelBulkPatchResponse,
    SegOrgLevelBulkPatchResult,
    SegOrgLevelBulkPatchRow,
    SegOrgLevelPatchErrorCode,
    SegOrgLevelService,
} from 'src/app/services/seg-org-level.service';
import { SegService } from 'src/app/services/seg.service';
import { ShiftService } from 'src/app/services/shift.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { IFormDialogData, IOrganizationLevelEntity } from 'src/app/shared/form/form.component';
import { ILookup } from 'src/app/shared/form/lookup.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

export interface ISegSiteDialogData extends IFormDialogData, IOrganizationLevelEntity {
    segId?: string;
    // Optional: when set (bulk edit from SEG list selection), the scope matrix only shows these SEGs
    segIds?: string[];
}

type StepType = 1 | 2;
type OrgLevelTypeFilter = 'all' | 'site' | 'department' | 'other';
type CellState = 'clean' | 'dirty' | 'saving' | 'saved' | 'error';
type EntryColumn =
    | 'shiftId'
    | 'numberOfWorkers'
    | 'numberOfContractors'
    | 'shiftLengthHours'
    | 'avgWeeklyHours';
type EntrySortColumn = 'segName' | 'orgLevelName' | EntryColumn;
type SortDirection = 'asc' | 'desc';

interface MatrixSeg {
    id: string;
    name: string;
}

interface MatrixOrgLevel {
    id: string;
    name: string;
    depth: number;
    orgLevelType: number;
}

interface MatrixCell {
    key: string;
    segId: string;
    orgLevelId: string;
    selected: boolean;
    existing: boolean;
}

interface MatrixRowView {
    seg: MatrixSeg;
    cells: MatrixCell[];
}

interface EntryRow {
    key: string;
    segId: string;
    segName: string;
    orgLevelId: string;
    orgLevelName: string;
    isNew: boolean;
    shiftId: string | null;
    numberOfWorkers: number | null;
    numberOfContractors: number | null;
    shiftLengthHours: number | null;
    avgWeeklyHours: number | null;
    hasLinkedShift: boolean;
    linkedShiftName: string;
    cellState: Record<EntryColumn, CellState>;
    cellErrors: Partial<Record<EntryColumn, string>>;
    lastSaved: Record<EntryColumn, string | number | null>;
    rowState: 'none' | 'saving' | 'error';
}

interface SaveRequest {
    rowKey: string;
    column: EntryColumn;
    previousValue: string | number | null;
}

interface UndoEntry {
    rowKey: string;
    column: EntryColumn;
    previousValue: string | number | null;
    nextValue: string | number | null;
}

@Component({
  standalone: false,
    selector: 'app-seg-site-dialog',
    templateUrl: './seg-site-dialog.component.html',
    styleUrls: ['./seg-site-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SegSiteDialogComponent implements OnInit, AfterViewChecked, OnDestroy {
    @ViewChild(CdkVirtualScrollViewport) entryGridViewport?: CdkVirtualScrollViewport;
    @ViewChild('entryGridHeaderWrap') entryGridHeaderWrap?: ElementRef<HTMLElement>;
    @ViewChild('entryGridHeaderGutter') entryGridHeaderGutter?: ElementRef<HTMLElement>;
    @ViewChild('applyAllFocusTarget') applyAllFocusTarget?: ElementRef<HTMLElement>;

    // Fixed row height (px) required by cdk-virtual-scroll-viewport's fixed-size strategy
    readonly entryRowHeight = 56;

    readonly columns: ReadonlyArray<EntryColumn> = [
        'shiftId',
        'numberOfWorkers',
        'numberOfContractors',
        'shiftLengthHours',
        'avgWeeklyHours',
    ];

    readonly columnLabels: Record<EntryColumn, string> = {
        shiftId: 'Shift',
        numberOfWorkers: 'Number of Employees',
        numberOfContractors: 'Number of Contractors',
        shiftLengthHours: 'Shift Length (hrs)',
        avgWeeklyHours: 'Avg Weekly Hours',
    };

    readonly applyAllShortLabels: Record<EntryColumn, string> = {
        shiftId: 'Shift',
        numberOfWorkers: '# Employees',
        numberOfContractors: '# Contractors',
        shiftLengthHours: 'Shift Length (hrs)',
        avgWeeklyHours: 'Avg Weekly Hrs',
    };

    isLoading = false;
    isCreatingPairs = false;
    activeStep: StepType = 1;

    matrixSegs: MatrixSeg[] = [];
    matrixOrgLevels: MatrixOrgLevel[] = [];
    matrixRows: MatrixRowView[] = [];
    matrixCells = new Map<string, MatrixCell>();
    matrixExistingSelectedCount = 0;
    matrixNewSelectedCount = 0;
    matrixSelectAll = false;
    rowSelection: Record<string, boolean> = {};
    colSelection: Record<string, boolean> = {};

    orgLevelTypeFilter: OrgLevelTypeFilter = 'all';
    orgLevelVisibility: Record<string, boolean> = {};

    entryRows: EntryRow[] = [];
    filteredEntryRows: EntryRow[] = [];
    filterText = '';
    sortColumn: EntrySortColumn = 'segName';
    sortDirection: SortDirection = 'asc';

    shifts: ILookup[] = [];
    shiftById = new Map<string, string>();
    private readonly shiftDataById = new Map<string, Shift>();
    private readonly shiftFetches = new Map<string, Observable<Shift>>();

    applyAllColumn: EntryColumn | null = null;
    applyAllValue: string | null = null;
    applyAllApplicableCount = 0;
    applyAllSkippedCount = 0;
    applyAllPosition = { top: 0, left: 0 };


    showErrorsOnly = false;
    anyErrors = false;

    selectedSiteName = '';

    isMatrixReady = false;
    matrixInitProgress = 0;

    private organizationLevelById = new Map<string, OrganizationLevel>();
    private readonly destroy$ = new Subject<void>();
    private readonly saveQueue$ = new Subject<SaveRequest>();
    private readonly savedTimers = new Map<string, number>();
    private readonly entryRowMap = new Map<string, EntryRow>();
    private readonly undoStack: UndoEntry[] = [];
    private recomputeTimer: number | null = null;
    private changedSegIds = new Set<string>();
    private changedOrgLevelIds = new Set<string>();
    private matrixInitTimer: number | null = null;
    private headerSyncViewport?: CdkVirtualScrollViewport;
    private headerSyncSubscription?: Subscription;

    // DASH-3147: the full shift records are prefetched once so Shift Length / Avg Weekly Hours
    // resolve from local state the instant a shift is picked, with no server round-trip.
    private static readonly shiftDetailPageSize = 1000;

    readonly trackByIndex = trackByIndex;

    private readonly matrixIdentityColWidth = 220;
    private readonly matrixOrgLevelColWidth = 160;

    get matrixTableWidthPx(): number {
        const visibleOrgLevels = this.matrixOrgLevels.filter((orgLevel) => this.orgLevelVisibility[orgLevel.id]).length;
        return this.matrixIdentityColWidth + visibleOrgLevels * this.matrixOrgLevelColWidth;
    }

    constructor(
        private dialogRef: MatDialogRef<SegSiteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ISegSiteDialogData,
        private segOrgLevelService: SegOrgLevelService,
        private segService: SegService,
        private organizationLevelService: OrganizationLevelService,
        private shiftService: ShiftService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.isLoading = true;

        this.saveQueue$
            .pipe(
                mergeMap((request) => this.executeSave(request), 6),
                takeUntil(this.destroy$),
            )
            .subscribe();

        forkJoin({
            segs: this.segService.getAll(),
            organizationLevels: this.organizationLevelService.getAll(),
            // DASH-3095: scope existing pairs to this site's org-level subtree server-side
            // (parentOrgLevelId) instead of fetching everything and filtering client-side.
            existingPairs: this.segOrgLevelService.getAssociations(undefined, this.data.organizationLevelId),
            shifts: this.shiftService.getAll(),
            // GetAll returns id + name only; GetList carries the configured shift length and
            // average weekly hours the derived columns display. A failure here is not fatal -
            // the per-shift getById fallback in applyLinkedShift covers it.
            shiftDetails: this.shiftService
                .get('', nameOf<Shift>((x) => x.name), 'asc', 0, SegSiteDialogComponent.shiftDetailPageSize, '')
                .pipe(
                    map((response) => response?.data ?? []),
                    catchError(() => of<Shift[]>([])),
                ),
        }).subscribe({
            next: ({ segs, organizationLevels, existingPairs, shifts, shiftDetails }) => {
                this.initializeShifts(shifts, shiftDetails);
                this.initializeMatrix(segs, organizationLevels, existingPairs);
                this.isLoading = false;
                this.cdr.markForCheck();
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
                this.cdr.markForCheck();
            },
        });
    }

    // The header row lives outside the virtual-scroll viewport so it stays pinned; keep its
    // horizontal offset in sync with the rows, which scroll in the viewport below it.
    ngAfterViewChecked(): void {
        if (this.entryGridViewport === this.headerSyncViewport) {
            return;
        }

        this.headerSyncSubscription?.unsubscribe();
        this.headerSyncSubscription = undefined;
        this.headerSyncViewport = this.entryGridViewport;

        if (!this.entryGridViewport) {
            return;
        }

        this.headerSyncSubscription = this.entryGridViewport
            .elementScrolled()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.syncHeaderHorizontalScroll());
        this.syncHeaderHorizontalScroll();
    }

    private syncHeaderHorizontalScroll(): void {
        const viewportElement = this.entryGridViewport?.elementRef.nativeElement;
        const headerWrapElement = this.entryGridHeaderWrap?.nativeElement;
        if (!viewportElement || !headerWrapElement) {
            return;
        }

        const gutterElement = this.entryGridHeaderGutter?.nativeElement;
        if (gutterElement) {
            // Reserve the viewport's vertical scrollbar width so both scrollers share a max scroll offset
            gutterElement.style.width = `${viewportElement.offsetWidth - viewportElement.clientWidth}px`;
        }

        headerWrapElement.scrollLeft = viewportElement.scrollLeft;
    }

    ngOnDestroy(): void {
        this.headerSyncSubscription?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
        this.savedTimers.forEach((id) => window.clearTimeout(id));
        this.savedTimers.clear();
        if (this.recomputeTimer !== null) {
            window.clearTimeout(this.recomputeTimer);
        }
        if (this.matrixInitTimer !== null) {
            window.clearTimeout(this.matrixInitTimer);
        }
    }

    cancel(): void {
        this.dialogRef.close('');
    }

    done(): void {
        this.dialogRef.close(true);
    }

    backToScope(): void {
        this.activeStep = 1;
    }

    continueToGrid(): void {
        if (this.isCreatingPairs) {
            return;
        }

        const selectedCells = Array.from(this.matrixCells.values()).filter((cell) => cell.selected);
        if (selectedCells.length === 0) {
            this.toastService.showWarning(
                this.translateService.getDynamicTranslation('Select at least one SEG and org level pair'),
            );
            return;
        }

        const newCells = selectedCells.filter((cell) => !cell.existing);

        this.isCreatingPairs = true;
        const createStream = this.createSelectedPairs(newCells);

        createStream
            .pipe(
                takeUntil(this.destroy$),
                mergeMap(() => this.loadEntryRowsFromSelection()),
                finalize(() => {
                    this.isCreatingPairs = false;
                    this.cdr.markForCheck();
                }),
            )
            .subscribe({
                next: () => {
                    this.activeStep = 2;
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    this.toastService.showError(error);
                    this.cdr.markForCheck();
                },
            });
    }

    /**
     * Create selected SEG-OrgLevel pairs with aggressive batching to prevent main-thread saturation.
     * Spec: AC "If multiple API calls are required, they are batched or rate-limited to prevent
     * main-thread saturation — they must not all fire simultaneously in a single synchronous burst."
     * 
     * Uses THREE optimizations:
     * 1. Smaller batches (100 pairs) to reduce individual payload size and parsing time
     * 2. Parallel requests with concurrency limit (3) using mergeMap instead of sequential concatMap
     *    This allows multiple in-flight requests without overwhelming the network or main thread
     * 3. Cross-product batching: splits combinations of SEGs and OrgLevels, not just by SEG count
     * 
     * Example: 500 SEGs × 50 OrgLevels = 25,000 pairs
     * - Without batching: 1 request with 25k items → massive payload → main thread blocks → browser crash
     * - With this approach: 250 requests of 100 items each, max 3 in flight simultaneously
     *   → individual payloads are small → processing is fast → no main thread blocking
     */
    private createSelectedPairs(newCells: MatrixCell[]): Observable<void> {
        if (!newCells.length) {
            return of(undefined);
        }

        // Extract unique SEG and Org Level IDs
        const segIds = Array.from(new Set(newCells.map((cell) => cell.segId)));
        const orgLevelIds = Array.from(new Set(newCells.map((cell) => cell.orgLevelId)));

        // Create batches using cross-product approach: max 100 pairs per batch
        // This prevents huge payloads that block the main thread
        const batchSize = 100; // Much smaller than 500 to reduce per-request processing time
        const batches: Array<{ segIds: string[]; orgLevelIds: string[] }> = [];

        // Distribute segIds and orgLevelIds across batches to keep individual requests small
        for (let i = 0; i < segIds.length; i += batchSize) {
            const segBatch = segIds.slice(i, Math.min(i + batchSize, segIds.length));
            batches.push({ segIds: segBatch, orgLevelIds });
        }

        // Use mergeMap with concurrency limit = 3 to send multiple requests in parallel,
        // but not all at once. This keeps the network responsive while preventing saturation.
        // Spec AC: "they are batched or rate-limited to prevent main-thread saturation"
        return from(batches).pipe(
            mergeMap(
                (batch) =>
                    this.segOrgLevelService.bulkAddSegOrgLevels(batch).pipe(
                        catchError((error) => throwError(() => error)),
                    ),
                3, // Concurrency limit: max 3 requests in flight at any time
            ),
            last(), // wait for all batches to complete before emitting once
            map(() => undefined),
        );
    }

    setOrgLevelTypeFilter(filter: OrgLevelTypeFilter): void {
        this.orgLevelTypeFilter = filter;
        this.applyOrgLevelTypeFilter();
    }

    private applyOrgLevelTypeFilter(): void {
        const visibility: Record<string, boolean> = {};
        this.matrixOrgLevels.forEach((orgLevel) => {
            visibility[orgLevel.id] =
                this.orgLevelTypeFilter === 'all' || this.getOrgLevelTypeGroup(orgLevel.orgLevelType) === this.orgLevelTypeFilter;
        });
        this.orgLevelVisibility = visibility;
    }

    private getOrgLevelTypeGroup(orgLevelType: number): OrgLevelTypeFilter {
        if (orgLevelType === OrgLevelTypeEnum.Site) {
            return 'site';
        }
        if (orgLevelType === OrgLevelTypeEnum.Department) {
            return 'department';
        }
        return 'other';
    }

    toggleCell(segId: string, orgLevelId: string): void {
        const cell = this.matrixCells.get(this.getCellKey(segId, orgLevelId));
        if (!cell || cell.existing) {
            return;
        }

        cell.selected = !cell.selected;
        // Track only the affected row and column for targeted recalculation
        this.changedSegIds.add(segId);
        this.changedOrgLevelIds.add(orgLevelId);
        this.scheduleMatrixStateRecompute();
    }

    toggleSegRow(segId: string, checked: boolean): void {
        this.matrixOrgLevels.forEach((orgLevel) => {
            const cell = this.matrixCells.get(this.getCellKey(segId, orgLevel.id));
            if (!cell || cell.existing) {
                return;
            }
            cell.selected = checked;
            this.changedOrgLevelIds.add(orgLevel.id);
        });
        this.changedSegIds.add(segId);
        this.scheduleMatrixStateRecompute();
    }

    toggleOrgLevelColumn(orgLevelId: string, checked: boolean): void {
        this.matrixSegs.forEach((seg) => {
            const cell = this.matrixCells.get(this.getCellKey(seg.id, orgLevelId));
            if (!cell || cell.existing) {
                return;
            }
            cell.selected = checked;
            this.changedSegIds.add(seg.id);
        });
        this.changedOrgLevelIds.add(orgLevelId);
        this.scheduleMatrixStateRecompute();
    }

    toggleMatrixSelectAll(checked: boolean): void {
        this.matrixCells.forEach((cell) => {
            if (!cell.existing) {
                cell.selected = checked;
                this.changedSegIds.add(cell.segId);
                this.changedOrgLevelIds.add(cell.orgLevelId);
            }
        });

        this.scheduleMatrixStateRecompute();
    }

    onFilterChange(value: string): void {
        this.filterText = value.toLowerCase().trim();
        this.updateFilteredRows();
        if (this.applyAllColumn) {
            this.computeApplyAllCounts();
        }
    }

    toggleShowErrorsOnly(checked: boolean): void {
        this.showErrorsOnly = checked;
        this.updateFilteredRows();
        if (this.applyAllColumn) {
            this.computeApplyAllCounts();
        }
    }

    jumpToFirstError(): void {
        const firstErrorRow = this.entryRows.find((row) => this.hasRowErrors(row));
        if (!firstErrorRow) {
            return;
        }

        // Scroll to the first error row in the virtual scroll viewport
        const rowIndex = this.entryRows.indexOf(firstErrorRow);
        if (rowIndex >= 0 && this.entryGridViewport) {
            this.entryGridViewport.scrollToIndex(rowIndex, 'auto');
        }
    }

    setSort(column: EntrySortColumn): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }

        this.updateFilteredRows();
    }

    openApplyAll(column: EntryColumn, event: MouseEvent): void {
        // Close any previously open dropdown
        if (this.applyAllColumn !== column) {
            this.applyAllColumn = column;
            this.applyAllValue = this.getApplyAllDefaultValue(column);
            this.computeApplyAllCounts();
            this.positionApplyAll(event.currentTarget as HTMLElement);

            // Focus the input/select after view update
            setTimeout(() => {
                const el = this.applyAllFocusTarget?.nativeElement;
                el?.focus();
                if (el instanceof HTMLInputElement) {
                    el.select();
                }
            }, 0);
        }
    }

    private positionApplyAll(anchor: HTMLElement): void {
        const popoverWidth = 260;
        const popoverHeight = 200;
        const margin = 8;
        const anchorRect = anchor.getBoundingClientRect();

        let top = anchorRect.bottom + margin;
        let left = anchorRect.left;

        if (top + popoverHeight > window.innerHeight - margin) {
            top = Math.max(margin, anchorRect.top - popoverHeight - margin);
        }
        if (left + popoverWidth > window.innerWidth - margin) {
            left = Math.max(margin, window.innerWidth - popoverWidth - margin);
        }

        this.applyAllPosition = { top, left };
    }

    cancelApplyAll(): void {
        this.applyAllColumn = null;
        this.applyAllValue = null;
    }

    applyAll(): void {
        if (!this.applyAllColumn) {
            return;
        }

        const column = this.applyAllColumn;
        const rawApplyAllValue = this.applyAllValue;
        const applicableRows = this.getApplicableRowsForApplyAll(column);
        this.cancelApplyAll();

        if (!applicableRows.length) {
            return;
        }

        const nextValue = this.toColumnValue(column, rawApplyAllValue);

        // Snapshot previous values before mutating for undo + error revert
        const previousByKey = new Map(applicableRows.map((row) => [row.key, this.getColumnValue(row, column)]));

        applicableRows.forEach((row) => {
            const previousValue = previousByKey.get(row.key) ?? null;
            this.undoStack.push({ rowKey: row.key, column, previousValue, nextValue });
            this.setColumnValue(row, column, nextValue);
            this.setCellState(row, column, 'saving');
            row.rowState = 'saving';
        });

        // Spec §3: "Set for all rows" fires ONE BulkPatch call with all rows
        const rowPayloads = applicableRows
            .map((row) => this.buildBulkPatchPayload(row, column)?.rows[0])
            .filter((r): r is SegOrgLevelBulkPatchRow => !!r);

        if (!rowPayloads.length) {
            applicableRows.forEach((row) => {
                this.setCellState(row, column, 'clean');
                row.rowState = 'none';
            });
            return;
        }

        this.segOrgLevelService
            .bulkPatch({ rows: rowPayloads })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    applicableRows.forEach((row) => {
                        const result = response.results?.find(
                            (r) => r.segId === row.segId && r.orgLevelId === row.orgLevelId,
                        );
                        const hasError =
                            result?.success === false ||
                            (result?.errorCode !== undefined &&
                                result.errorCode !== SegOrgLevelPatchErrorCode.None) ||
                            !!result?.error;

                        if (hasError) {
                            this.handleSaveError(
                                row,
                                column,
                                previousByKey.get(row.key) ?? null,
                                result?.error ?? this.translateService.getDynamicTranslation('Save failed'),
                            );
                        } else {
                            row.lastSaved[column] = this.getColumnValue(row, column);
                            this.setCellState(row, column, 'saved');
                            delete row.cellErrors[column];
                            this.refreshAnyErrors();

                            if (column === 'shiftId') {
                                this.applySavedShiftValues(row, result);
                            }

                            this.scheduleClearSavedState(row, column);
                        }

                        row.rowState = this.hasRowErrors(row) ? 'error' : 'none';
                    });
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    const message = this.resolveErrorMessage(error);
                    applicableRows.forEach((row) => {
                        this.handleSaveError(row, column, previousByKey.get(row.key) ?? null, message);
                        row.rowState = 'error';
                    });
                    this.cdr.markForCheck();
                },
            });
    }

    onCellFocus(row: EntryRow, column: EntryColumn): void {
        row.lastSaved[column] = this.getColumnValue(row, column);
    }

    onCellValueChange(row: EntryRow, column: EntryColumn): void {
        this.markCellDirty(row, column);
        delete row.cellErrors[column];
        this.refreshAnyErrors();

        if (column === 'shiftId') {
            this.applyLinkedShift(row);
        }
    }

    onCellBlur(row: EntryRow, column: EntryColumn): void {
        const previousValue = row.lastSaved[column];
        const nextValue = this.getColumnValue(row, column);
        if (this.areValuesEqual(previousValue, nextValue)) {
            this.setCellState(row, column, 'clean');
            return;
        }

        this.undoStack.push({
            rowKey: row.key,
            column,
            previousValue,
            nextValue,
        });
        this.saveQueue$.next({
            rowKey: row.key,
            column,
            previousValue,
        });
    }

    onCellArrowNav(event: KeyboardEvent, rowIndex: number, column: EntryColumn): void {
        if (!['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
            return;
        }

        const step = event.key === 'ArrowUp' ? -1 : 1;
        const targetIndex = rowIndex + step;
        if (targetIndex < 0 || targetIndex >= this.filteredEntryRows.length) {
            return;
        }

        event.preventDefault();

        const targetRow = this.filteredEntryRows[targetIndex];
        const cellId = `cell-${targetRow.key}-${column}`;
        const existingElement = document.getElementById(cellId) as HTMLInputElement | HTMLSelectElement | null;
        if (existingElement) {
            this.focusCellElement(existingElement);
            return;
        }

        // Target row isn't rendered yet (virtual scroll) — scroll it into view, then focus once rendered.
        if (!this.entryGridViewport) {
            return;
        }

        this.entryGridViewport.scrollToIndex(targetIndex, 'auto');
        this.entryGridViewport
            .renderedRangeStream.pipe(take(1), takeUntil(this.destroy$))
            .subscribe(() => {
                const element = document.getElementById(cellId) as HTMLInputElement | HTMLSelectElement | null;
                if (element) {
                    this.focusCellElement(element);
                }
            });
    }

    private focusCellElement(element: HTMLInputElement | HTMLSelectElement): void {
        element.focus();
        if (element instanceof HTMLInputElement) {
            element.select();
        }
    }

    retryCell(row: EntryRow, column: EntryColumn): void {
        const previousValue = row.lastSaved[column];
        this.markCellDirty(row, column);
        this.saveQueue$.next({
            rowKey: row.key,
            column,
            previousValue,
        });
    }

    @HostListener('document:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        if (this.activeStep !== 2) {
            return;
        }

        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
            const last = this.undoStack.pop();
            if (!last) {
                return;
            }

            const row = this.entryRowMap.get(last.rowKey);
            if (!row) {
                return;
            }

            event.preventDefault();
            this.setColumnValue(row, last.column, last.previousValue);
            this.markCellDirty(row, last.column);
            this.saveQueue$.next({
                rowKey: row.key,
                column: last.column,
                previousValue: last.nextValue,
            });
        }
    }

    /**
     * DASH-3147: mirrors the selected shift's configuration onto the row's read-only
     * Shift Length / Avg Weekly Hours cells the moment the shift changes. The values come from
     * the prefetched shift records, so the cells populate without waiting for the autosave
     * response; a shift missing from that prefetch is fetched once and cached.
     */
    private applyLinkedShift(row: EntryRow): void {
        const shiftId = row.shiftId;
        if (!shiftId) {
            row.hasLinkedShift = false;
            row.linkedShiftName = '';
            return;
        }

        row.hasLinkedShift = true;
        row.linkedShiftName = this.shiftById.get(shiftId) ?? '';

        const shift = this.shiftDataById.get(shiftId);
        if (shift) {
            this.applyShiftValues(row, shift);
            return;
        }

        this.fetchShift(shiftId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((fetchedShift) => {
                // Ignore a stale response if the row's selection changed while in flight.
                if (row.shiftId === shiftId) {
                    this.applyShiftValues(row, fetchedShift);
                }
                this.cdr.markForCheck();
            });
    }

    /**
     * One shared request per uncached shift. "Set all" links the same shift on every visible row
     * at once, so a per-row lookup would fire one request per row and saturate the browser the way
     * the Add to Site dialog did before DASH-3068. The failure is reported once by the shared
     * request rather than once per waiting row.
     */
    private fetchShift(shiftId: string): Observable<Shift> {
        const inFlight = this.shiftFetches.get(shiftId);
        if (inFlight) {
            return inFlight;
        }

        const request$ = this.shiftService.getById(shiftId).pipe(
            filter((shift): shift is Shift => !!shift),
            tap((shift) => this.shiftDataById.set(shiftId, shift)),
            catchError((error) => {
                this.toastService.showError(error);
                return EMPTY;
            }),
            finalize(() => this.shiftFetches.delete(shiftId)),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

        this.shiftFetches.set(shiftId, request$);
        return request$;
    }

    private applyShiftValues(row: EntryRow, shift: Shift): void {
        row.shiftLengthHours = this.resolveShiftLengthHours(shift);
        row.avgWeeklyHours = shift.averageWeeklyHours ?? null;
        row.lastSaved.shiftLengthHours = row.shiftLengthHours;
        row.lastSaved.avgWeeklyHours = row.avgWeeklyHours;
        this.setCellState(row, 'shiftLengthHours', 'clean');
        this.setCellState(row, 'avgWeeklyHours', 'clean');
    }

    /**
     * A shift only persists shiftLengthHours once its start/end times are edited, so a shift
     * configured before that still carries the times alone. Deriving the length from them keeps
     * the column showing the shift's real length instead of a dash.
     */
    private resolveShiftLengthHours(shift: Shift | undefined): number | null {
        if (!shift) {
            return null;
        }

        return shift.shiftLengthHours ?? computeShiftLengthHours(shift.startTime, shift.endTime);
    }

    /**
     * Reconciles the derived cells with the autosave response. The server echoes the values only
     * when it resolves them, so a missing or null echo keeps whatever was already resolved
     * locally instead of blanking the cells back to a hyphen.
     */
    private applySavedShiftValues(row: EntryRow, result: SegOrgLevelBulkPatchResult | undefined): void {
        this.applyLinkedShift(row);

        if (!row.shiftId) {
            return;
        }

        if (result?.shiftLengthHours != null) {
            row.shiftLengthHours = result.shiftLengthHours;
            row.lastSaved.shiftLengthHours = result.shiftLengthHours;
            this.setCellState(row, 'shiftLengthHours', 'clean');
        }

        if (result?.avgWeeklyHours != null) {
            row.avgWeeklyHours = result.avgWeeklyHours;
            row.lastSaved.avgWeeklyHours = result.avgWeeklyHours;
            this.setCellState(row, 'avgWeeklyHours', 'clean');
        }
    }

    private initializeShifts(shiftLookups: ILookup[], shiftDetails: Shift[]): void {
        this.shifts = shiftLookups
            .map((shift) => ({
                id: String(shift.id),
                name: String(shift.name),
            }))
            .sort((left, right) => left.name.localeCompare(right.name));

        this.shiftById.clear();
        this.shifts.forEach((shift) => {
            this.shiftById.set(String(shift.id), shift.name);
        });

        this.shiftDataById.clear();
        shiftDetails.forEach((shift) => {
            this.shiftDataById.set(String(shift.id), shift);
        });
    }

    private initializeMatrix(
        segLookups: ILookup[],
        organizationLevels: OrganizationLevel[],
        existingPairs: SegOrgLevel[],
    ): void {
        const selectedOrgId = this.data.organizationLevelId;
        this.organizationLevelById = new Map(organizationLevels.map((orgLevel) => [orgLevel.id, orgLevel]));
        this.selectedSiteName = this.organizationLevelById.get(selectedOrgId)?.name ?? '';

        const orgTree = toOrganizationTree(organizationLevels);
        const scopeOrgIds = getDescendantOrSelfIds(orgTree, selectedOrgId);
        if (!scopeOrgIds.size && selectedOrgId) {
            scopeOrgIds.add(selectedOrgId);
        }

        this.matrixOrgLevels = this.flattenScopedOrgLevels(organizationLevels, selectedOrgId, scopeOrgIds);
        this.applyOrgLevelTypeFilter();

        // DASH-3078: when opened for specific SEG(s) (row action, bulk edit, or SEG details popup),
        // only show those SEGs' rows instead of every SEG in the system.
        const restrictToSegIds = this.data.segIds?.length ? new Set(this.data.segIds) : this.data.segId ? new Set([this.data.segId]) : null;

        this.matrixSegs = segLookups
            .filter((seg) => !restrictToSegIds || restrictToSegIds.has(String(seg.id)))
            .map((seg) => ({ id: String(seg.id), name: String(seg.name) }))
            .sort((left, right) => left.name.localeCompare(right.name));

        // DASH-3095: existingPairs are already scoped to this site's org-level subtree server-side
        // via getAssociations(parentOrgLevelId), so the previous client-side allowedOrgLevelIds
        // filter is redundant and has been removed.
        const existingPairKeys = new Set(
            existingPairs.map((pair) => this.getCellKey(pair.segId, pair.organizationLevelId)),
        );

        this.matrixCells.clear();
        this.matrixRows = [];
        this.isMatrixReady = false;

        // Async matrix initialization: create cells in batches to avoid blocking main thread
        // With 10,000+ cells, synchronous creation hangs the dialog. Batching allows browser to render between batches.
        this.initializeMatrixAsync(0, segLookups, existingPairKeys);
    }

    /**
     * Asynchronously create matrix cells in batches to keep the UI responsive.
     * Each batch creates ~50 cells, then yields to the browser for rendering/input handling.
     * This prevents the "dialog hanging" feeling when loading 1000+ items.
     */
    private initializeMatrixAsync(
        batchIndex: number,
        segLookups: ILookup[],
        existingPairKeys: Set<string>,
    ): void {
        const batchSize = 50; // Create 50 cells per batch
        const totalBatches = Math.ceil(this.matrixSegs.length / batchSize);

        // Create one batch of cells
        const startSegIdx = batchIndex * batchSize;
        const endSegIdx = Math.min(startSegIdx + batchSize, this.matrixSegs.length);

        for (let i = startSegIdx; i < endSegIdx; i++) {
            const seg = this.matrixSegs[i];
            const cells: MatrixCell[] = [];

            for (const orgLevel of this.matrixOrgLevels) {
                const key = this.getCellKey(seg.id, orgLevel.id);
                const isExisting = existingPairKeys.has(key);
                const shouldSelect =
                    isExisting ||
                    (seg.id === this.data.segId && orgLevel.id === this.data.organizationLevelId);
                const cell: MatrixCell = {
                    key,
                    segId: seg.id,
                    orgLevelId: orgLevel.id,
                    selected: shouldSelect,
                    existing: isExisting,
                };
                this.matrixCells.set(key, cell);
                cells.push(cell);
            }

            // Create row for this segment
            this.matrixRows.push({
                seg,
                cells,
            });
        }

        // Update progress
        this.matrixInitProgress = Math.round((endSegIdx / this.matrixSegs.length) * 100);
        this.cdr.markForCheck();

        // If more batches to process, schedule next batch asynchronously
        if (batchIndex < totalBatches - 1) {
            // Use setTimeout with 0 to defer to next event loop, allowing browser to render
            this.matrixInitTimer = window.setTimeout(() => {
                this.matrixInitTimer = null;
                this.initializeMatrixAsync(batchIndex + 1, segLookups, existingPairKeys);
            }, 0);
        } else {
            // All batches complete - now do the expensive recomputation
            this.matrixInitTimer = null;
            this.recomputeMatrixSelectionState();
            this.isMatrixReady = true;
            this.matrixInitProgress = 100;
            this.cdr.markForCheck();
        }
    }

    private loadEntryRowsFromSelection(): Observable<void> {
        const selectedCells = Array.from(this.matrixCells.values()).filter((cell) => cell.selected);
        const selectedSegIds = new Set(selectedCells.map((cell) => cell.segId));
        const selectedOrgIds = new Set(selectedCells.map((cell) => cell.orgLevelId));

        if (!selectedSegIds.size) {
            this.entryRows = [];
            this.filteredEntryRows = [];
            this.entryRowMap.clear();
            return of(undefined);
        }

        return this.segOrgLevelService.getAssociations(undefined, this.data.organizationLevelId).pipe(
            map((pairs) => {
                const pairMap = new Map<string, SegOrgLevel>();
                pairs.forEach((pair) => {
                    if (!selectedSegIds.has(pair.segId) || !selectedOrgIds.has(pair.organizationLevelId)) {
                        return;
                    }
                    pairMap.set(this.getCellKey(pair.segId, pair.organizationLevelId), pair);
                });

                const segNameById = new Map(this.matrixSegs.map((seg) => [seg.id, seg.name]));
                this.entryRows = selectedCells
                    .map((cell) => {
                        const pair = pairMap.get(cell.key);
                        return this.createEntryRow(
                            cell,
                            pair,
                            segNameById.get(cell.segId) ?? '',
                            this.organizationLevelById.get(cell.orgLevelId)?.name ?? '',
                        );
                    })
                    .sort((left, right) => {
                        const segSort = left.segName.localeCompare(right.segName);
                        if (segSort !== 0) {
                            return segSort;
                        }
                        return left.orgLevelName.localeCompare(right.orgLevelName);
                    });

                this.entryRowMap.clear();
                this.entryRows.forEach((row) => {
                    this.entryRowMap.set(row.key, row);
                });

                this.updateFilteredRows();
            }),
        );
    }

    private createEntryRow(
        cell: MatrixCell,
        pair: SegOrgLevel | undefined,
        segName: string,
        orgLevelName: string,
    ): EntryRow {
        const shiftId = pair?.shiftId ? String(pair.shiftId) : null;
        const linkedShift = shiftId ? this.shiftDataById.get(shiftId) : undefined;
        const shiftLengthHours = pair?.shiftLengthHours ?? this.resolveShiftLengthHours(linkedShift);
        const avgWeeklyHours = pair?.avgWeeklyHours ?? linkedShift?.averageWeeklyHours ?? null;
        return {
            key: cell.key,
            segId: cell.segId,
            segName,
            orgLevelId: cell.orgLevelId,
            orgLevelName,
            isNew: !cell.existing,
            shiftId,
            numberOfWorkers: pair?.numberOfWorkers ?? null,
            numberOfContractors: pair?.numberOfContractors ?? null,
            shiftLengthHours,
            avgWeeklyHours,
            hasLinkedShift: !!shiftId,
            linkedShiftName: shiftId ? this.shiftById.get(shiftId) ?? '' : '',
            cellState: {
                shiftId: 'clean',
                numberOfWorkers: 'clean',
                numberOfContractors: 'clean',
                shiftLengthHours: 'clean',
                avgWeeklyHours: 'clean',
            },
            cellErrors: {},
            lastSaved: {
                shiftId,
                numberOfWorkers: pair?.numberOfWorkers ?? null,
                numberOfContractors: pair?.numberOfContractors ?? null,
                shiftLengthHours,
                avgWeeklyHours,
            },
            rowState: 'none',
        };
    }

    private executeSave(request: SaveRequest): Observable<void> {
        const row = this.entryRowMap.get(request.rowKey);
        if (!row) {
            return of(undefined);
        }

        const payload = this.buildBulkPatchPayload(row, request.column);
        if (!payload) {
            this.setCellState(row, request.column, 'clean');
            return of(undefined);
        }

        this.setCellState(row, request.column, 'saving');
        row.rowState = 'saving';

        return this.segOrgLevelService.bulkPatch(payload).pipe(
            tap((response) => {
                this.handleSaveSuccess(row, request.column, request.previousValue, response);
            }),
            catchError((error) => {
                const message = this.resolveErrorMessage(error);
                this.handleSaveError(row, request.column, request.previousValue, message);
                return of(undefined);
            }),
            map(() => undefined),
            finalize(() => {
                if (this.hasRowCellState(row, 'saving')) {
                    row.rowState = 'saving';
                    return;
                }

                row.rowState = this.hasRowErrors(row) ? 'error' : 'none';
            }),
        );
    }

    private buildBulkPatchPayload(row: EntryRow, column: EntryColumn): SegOrgLevelBulkPatchRequest | null {
        const rowPayload: SegOrgLevelBulkPatchRow = {
            segId: row.segId,
            orgLevelId: row.orgLevelId,
        };

        const value = this.getColumnValue(row, column);
        if (column === 'shiftId') {
            if (value === null || value === '') {
                rowPayload.clearFields = ['shiftId'];
            } else {
                rowPayload.shiftId = String(value);
            }
            return { rows: [rowPayload] };
        }

        if ((column === 'shiftLengthHours' || column === 'avgWeeklyHours') && row.shiftId) {
            return null;
        }

        if (value === null || value === '') {
            rowPayload.clearFields = [column];
            return { rows: [rowPayload] };
        }

        const numericValue = typeof value === 'number' ? value : Number(value);
        if (Number.isNaN(numericValue)) {
            return null;
        }

        if (column === 'numberOfWorkers') {
            rowPayload.numberOfWorkers = numericValue;
        } else if (column === 'numberOfContractors') {
            rowPayload.numberOfContractors = numericValue;
        } else if (column === 'shiftLengthHours') {
            rowPayload.shiftLengthHours = numericValue;
        } else if (column === 'avgWeeklyHours') {
            rowPayload.avgWeeklyHours = numericValue;
        }

        return { rows: [rowPayload] };
    }

    private handleSaveSuccess(
        row: EntryRow,
        column: EntryColumn,
        previousValue: string | number | null,
        response: SegOrgLevelBulkPatchResponse,
    ): void {
        const result = response.results?.[0];
        const hasError =
            result?.success === false ||
            (result?.errorCode !== undefined && result.errorCode !== SegOrgLevelPatchErrorCode.None) ||
            !!result?.error;
        if (hasError) {
            this.handleSaveError(
                row,
                column,
                previousValue,
                result?.error ?? this.translateService.getDynamicTranslation('Save failed'),
            );
            return;
        }

        const value = this.getColumnValue(row, column);
        row.lastSaved[column] = value;
        this.setCellState(row, column, 'saved');
        delete row.cellErrors[column];
        this.refreshAnyErrors();

        if (column === 'shiftId') {
            this.applySavedShiftValues(row, result);
        }

        this.scheduleClearSavedState(row, column);
    }

    private handleSaveError(
        row: EntryRow,
        column: EntryColumn,
        previousValue: string | number | null,
        message: string,
    ): void {
        if (column === 'shiftId') {
            row.shiftId = this.toNullableString(previousValue);
            this.applyLinkedShift(row);
        }

        row.cellErrors[column] = message;
        this.setCellState(row, column, 'error');
        this.refreshAnyErrors();
    }

    private setCellState(row: EntryRow, column: EntryColumn, state: CellState): void {
        row.cellState[column] = state;
    }

    private scheduleClearSavedState(row: EntryRow, column: EntryColumn): void {
        const key = `${row.key}-${column}`;
        window.clearTimeout(this.savedTimers.get(key));
        this.savedTimers.set(
            key,
            window.setTimeout(() => {
                if (row.cellState[column] === 'saved') {
                    this.setCellState(row, column, 'clean');
                }
                this.savedTimers.delete(key);
                this.cdr.markForCheck();
            }, 1600),
        );
    }

    private markCellDirty(row: EntryRow, column: EntryColumn): void {
        this.setCellState(row, column, 'dirty');
    }

    private hasRowCellState(row: EntryRow, state: CellState): boolean {
        return this.columns.some((column) => row.cellState[column] === state);
    }

    private hasRowErrors(row: EntryRow): boolean {
        return this.columns.some((column) => !!row.cellErrors[column]);
    }

    private refreshAnyErrors(): void {
        this.anyErrors = this.entryRows.some((row) => this.hasRowErrors(row));
    }

    private updateFilteredRows(): void {
        const rows = this.entryRows.filter((row) => {
            if (this.showErrorsOnly && !this.hasRowErrors(row)) {
                return false;
            }

            if (!this.filterText) {
                return true;
            }

            const text = `${row.segName} ${row.orgLevelName}`.toLowerCase();
            return text.includes(this.filterText);
        });

        this.filteredEntryRows = this.sortEntryRows(rows);
    }

    private sortEntryRows(rows: EntryRow[]): EntryRow[] {
        const direction = this.sortDirection === 'asc' ? 1 : -1;

        return [...rows].sort((left, right) => {
            const leftValue = this.getSortValue(left, this.sortColumn);
            const rightValue = this.getSortValue(right, this.sortColumn);

            const leftIsEmpty = leftValue === null || leftValue === '';
            const rightIsEmpty = rightValue === null || rightValue === '';

            if (leftIsEmpty && rightIsEmpty) {
                return left.key.localeCompare(right.key);
            }

            if (leftIsEmpty) {
                return 1;
            }

            if (rightIsEmpty) {
                return -1;
            }

            if (typeof leftValue === 'number' && typeof rightValue === 'number') {
                if (leftValue === rightValue) {
                    return left.key.localeCompare(right.key);
                }

                return leftValue > rightValue ? direction : -direction;
            }

            const leftText = String(leftValue).toLowerCase();
            const rightText = String(rightValue).toLowerCase();
            const compare = leftText.localeCompare(rightText);
            if (compare !== 0) {
                return compare * direction;
            }

            return left.key.localeCompare(right.key);
        });
    }

    private getSortValue(row: EntryRow, column: EntrySortColumn): string | number | null {
        if (column === 'segName') {
            return row.segName;
        }

        if (column === 'orgLevelName') {
            return row.orgLevelName;
        }

        if (column === 'shiftId') {
            return row.shiftId ? this.shiftById.get(row.shiftId) ?? row.shiftId : null;
        }

        if (column === 'numberOfWorkers') {
            return row.numberOfWorkers;
        }

        if (column === 'numberOfContractors') {
            return row.numberOfContractors;
        }

        if (column === 'shiftLengthHours') {
            return row.shiftLengthHours;
        }

        return row.avgWeeklyHours;
    }

    /**
     * Debounce recomputeMatrixSelectionState to avoid expensive recalculations on every checkbox click.
     * Batches all checkbox changes within a single macrotask and recomputes only affected rows/columns.
     * With targeted recalculation instead of full O(n*m) scan, checkbox clicks remain responsive even with 1000+ items.
     * Spec: AC "Checkbox interaction remains instant regardless of how many org levels are loaded"
     */
    private scheduleMatrixStateRecompute(): void {
        if (this.recomputeTimer !== null) {
            return; // Already scheduled, will run after current batch completes
        }

        this.recomputeTimer = window.setTimeout(() => {
            this.recomputeTimer = null;
            this.recomputeMatrixSelectionState();
            this.changedSegIds.clear();
            this.changedOrgLevelIds.clear();
            this.cdr.markForCheck();
        }, 0); // Use setTimeout(0) to defer to next macrotask, batching all synchronous checkbox changes
    }

    /**
     * Optimized recalculation: only update changed rows/columns, not the entire n*m matrix.
     * This reduces O(n*m) to O(c) where c = number of changed items, making 1000+ item sets responsive.
     */
    private recomputeMatrixSelectionState(): void {
        // If select-all was toggled or too many changes, do full recalculation
        const needsFullRecalc =
            this.changedSegIds.size === 0 && this.changedOrgLevelIds.size === 0;

        // Recount selected cells if needed for summary counts
        if (needsFullRecalc) {
            let existingCount = 0;
            let newCount = 0;

            this.matrixCells.forEach((cell) => {
                if (!cell.selected) {
                    return;
                }

                if (cell.existing) {
                    existingCount += 1;
                    return;
                }

                newCount += 1;
            });

            this.matrixExistingSelectedCount = existingCount;
            this.matrixNewSelectedCount = newCount;

            this.matrixSelectAll =
                this.matrixSegs.length > 0 &&
                this.matrixOrgLevels.length > 0 &&
                Array.from(this.matrixCells.values())
                    .filter((cell) => !cell.existing)
                    .every((cell) => cell.selected);

            // Recalculate row and column selections
            this.rowSelection = {};
            this.matrixSegs.forEach((seg) => {
                const selectable = this.matrixOrgLevels
                    .map((orgLevel) => this.matrixCells.get(this.getCellKey(seg.id, orgLevel.id)))
                    .filter((cell): cell is MatrixCell => !!cell && !cell.existing);
                this.rowSelection[seg.id] = selectable.length > 0 && selectable.every((cell) => cell.selected);
            });

            this.colSelection = {};
            this.matrixOrgLevels.forEach((orgLevel) => {
                const selectable = this.matrixSegs
                    .map((seg) => this.matrixCells.get(this.getCellKey(seg.id, orgLevel.id)))
                    .filter((cell): cell is MatrixCell => !!cell && !cell.existing);
                this.colSelection[orgLevel.id] = selectable.length > 0 && selectable.every((cell) => cell.selected);
            });
        } else {
            // Incremental update: only recalculate affected rows and columns
            // First, recount new/existing selected
            let existingCount = 0;
            let newCount = 0;
            this.matrixCells.forEach((cell) => {
                if (!cell.selected) return;
                if (cell.existing) existingCount += 1;
                else newCount += 1;
            });
            this.matrixExistingSelectedCount = existingCount;
            this.matrixNewSelectedCount = newCount;

            // Update only changed rows (SEGs)
            this.changedSegIds.forEach((segId) => {
                const selectable = this.matrixOrgLevels
                    .map((orgLevel) => this.matrixCells.get(this.getCellKey(segId, orgLevel.id)))
                    .filter((cell): cell is MatrixCell => !!cell && !cell.existing);
                this.rowSelection[segId] = selectable.length > 0 && selectable.every((cell) => cell.selected);
            });

            // Update only changed columns (Org Levels)
            this.changedOrgLevelIds.forEach((orgLevelId) => {
                const selectable = this.matrixSegs
                    .map((seg) => this.matrixCells.get(this.getCellKey(seg.id, orgLevelId)))
                    .filter((cell): cell is MatrixCell => !!cell && !cell.existing);
                this.colSelection[orgLevelId] = selectable.length > 0 && selectable.every((cell) => cell.selected);
            });

            // Quickly check if select-all should be updated
            this.matrixSelectAll =
                this.matrixSegs.length > 0 &&
                this.matrixOrgLevels.length > 0 &&
                Array.from(this.matrixCells.values())
                    .filter((cell) => !cell.existing)
                    .every((cell) => cell.selected);
        }
    }

    private flattenScopedOrgLevels(
        orgLevels: OrganizationLevel[],
        rootOrgId: string,
        scopedIds: Set<string>,
    ): MatrixOrgLevel[] {
        const childrenByParent = new Map<string, OrganizationLevel[]>();
        orgLevels.forEach((orgLevel) => {
            const parentKey = orgLevel.parentId ?? '__root__';
            if (!childrenByParent.has(parentKey)) {
                childrenByParent.set(parentKey, []);
            }
            childrenByParent.get(parentKey)?.push(orgLevel);
        });

        childrenByParent.forEach((children) => {
            children.sort((left, right) => left.name.localeCompare(right.name));
        });

        const root = this.organizationLevelById.get(rootOrgId);
        if (!root) {
            return orgLevels
                .filter((orgLevel) => scopedIds.has(orgLevel.id))
                .sort((left, right) => left.name.localeCompare(right.name))
                .map((orgLevel) => ({
                    id: orgLevel.id,
                    name: orgLevel.name,
                    depth: 0,
                    orgLevelType: orgLevel.orgLevelType ?? OrgLevelTypeEnum.Other,
                }));
        }

        const flat: MatrixOrgLevel[] = [];
        const walk = (node: OrganizationLevel, depth: number): void => {
            if (!scopedIds.has(node.id)) {
                return;
            }

            flat.push({
                id: node.id,
                name: node.name,
                depth,
                orgLevelType: node.orgLevelType ?? OrgLevelTypeEnum.Other,
            });
            const children = childrenByParent.get(node.id) ?? [];
            children.forEach((child) => {
                walk(child, depth + 1);
            });
        };

        walk(root, 0);
        return flat;
    }

    private computeApplyAllCounts(): void {
        if (!this.applyAllColumn) {
            this.applyAllApplicableCount = 0;
            this.applyAllSkippedCount = 0;
            return;
        }

        const visibleRows = this.filteredEntryRows;
        if (this.applyAllColumn === 'shiftLengthHours' || this.applyAllColumn === 'avgWeeklyHours') {
            const applicable = visibleRows.filter((row) => !row.shiftId).length;
            this.applyAllApplicableCount = applicable;
            this.applyAllSkippedCount = visibleRows.length - applicable;
            return;
        }

        this.applyAllApplicableCount = visibleRows.length;
        this.applyAllSkippedCount = 0;
    }

    private getApplicableRowsForApplyAll(column: EntryColumn): EntryRow[] {
        if (column !== 'shiftLengthHours' && column !== 'avgWeeklyHours') {
            return [...this.filteredEntryRows];
        }

        return this.filteredEntryRows.filter((row) => !row.shiftId);
    }

    private getApplyAllDefaultValue(column: EntryColumn): string | null {
        const firstRow = this.getApplicableRowsForApplyAll(column)[0];
        if (!firstRow) {
            return null;
        }

        const value = this.getColumnValue(firstRow, column);
        return value === null ? null : String(value);
    }

    private getColumnValue(row: EntryRow, column: EntryColumn): string | number | null {
        if (column === 'shiftId') {
            return row.shiftId;
        }
        if (column === 'numberOfWorkers') {
            return row.numberOfWorkers;
        }
        if (column === 'numberOfContractors') {
            return row.numberOfContractors;
        }
        if (column === 'shiftLengthHours') {
            return row.shiftLengthHours;
        }

        return row.avgWeeklyHours;
    }

    private setColumnValue(row: EntryRow, column: EntryColumn, value: string | number | null): void {
        if (column === 'shiftId') {
            row.shiftId = this.toNullableString(value);
            this.applyLinkedShift(row);
            return;
        }

        const numberValue = this.toNullableNumber(value);
        if (column === 'numberOfWorkers') {
            row.numberOfWorkers = numberValue;
            return;
        }
        if (column === 'numberOfContractors') {
            row.numberOfContractors = numberValue;
            return;
        }
        if (column === 'shiftLengthHours') {
            row.shiftLengthHours = numberValue;
            return;
        }

        row.avgWeeklyHours = numberValue;
    }

    private toColumnValue(column: EntryColumn, rawValue: string | null): string | number | null {
        if (column === 'shiftId') {
            return this.toNullableString(rawValue);
        }

        return this.toNullableNumber(rawValue);
    }

    private toNullableString(value: string | number | null): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        return String(value);
    }

    private toNullableNumber(value: string | number | null): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = typeof value === 'number' ? value : Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    }

    private areValuesEqual(left: string | number | null, right: string | number | null): boolean {
        if (left === right) {
            return true;
        }

        if ((left === null || left === '') && (right === null || right === '')) {
            return true;
        }

        return false;
    }

    private getCellKey(segId: string, orgLevelId: string): string {
        return `${segId}__${orgLevelId}`;
    }

    private resolveErrorMessage(error: unknown): string {
        if (typeof error === 'string') {
            return error;
        }

        if (typeof error !== 'object' || !error) {
            return this.translateService.getDynamicTranslation('Save failed');
        }

        const maybeError = error as {
            message?: string;
            error?: {
                message?: string;
                title?: string;
                errors?: Record<string, string[]>;
            };
        };

        const nestedMessage = maybeError.error?.message ?? maybeError.error?.title;
        if (nestedMessage) {
            return nestedMessage;
        }

        const nestedErrors = maybeError.error?.errors;
        if (nestedErrors) {
            const first = Object.values(nestedErrors).flat()[0];
            if (first) {
                return first;
            }
        }

        return maybeError.message ?? this.translateService.getDynamicTranslation('Save failed');
    }
}
